import {
  FormGroup,
  Validators,
  FormControl,
  AbstractControl
} from '@angular/forms';
import {
  Component,
  OnInit,
  ElementRef,
  Renderer2,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  LegService,
  PricingExtraService,
  TradesCaptureService,
  MarketsService,
  ProductsConfigService,
  UserStorageService,
  ConfigService
} from '@approot/services';
import { Utilities, DateUtilities } from '@approot/utilities';
import {
  CalculateFutureDatePayload,
  CurrencyInfo,
  DciTab
} from '@approot/models';
import dciJsonData from './dci-data.json';
import { DciTabComponent } from './dci-tab/dci-tab.component';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';
import { DciGridDefaultsStateKey, DciTabState } from '@approot/models';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

@Component({
  selector: 'app-dci',
  templateUrl: './dci.component.html',
  styleUrls: ['./dci.component.scss']
})
export class DciComponent implements OnInit {
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  @ViewChild(DciTabComponent, { static: false })
  public dciTabComponent: DciTabComponent;
  @Output() public loadStrategy = new EventEmitter<any>();
  public dciData = dciJsonData;
  public controlPanelForm: FormGroup;
  public showFooter = true;
  public entities: string[];
  public currencies: string[] = [];
  public tabs: any[] = [];
  public startDateInfo = { pending: false, info: '' };
  public dragging = false;
  public windowTop = 50;
  public windowLeft = 50;
  private allLoaded = false;
  private startDataRequestSub: Subscription;

  constructor(
    private configService: ConfigService,
    private elementRef: ElementRef,
    private legService: LegService,
    private marketsService: MarketsService,
    private pricingExtraService: PricingExtraService,
    private productsConfigService: ProductsConfigService,
    private renderer: Renderer2,
    private tradeCaptureService: TradesCaptureService,
    private userStorageService: UserStorageService,
    private winIdx: WindowIndexService
  ) {}

  public ngOnInit() {
    this.updateZIndex();
    let dciGridStorage = this.dataStorage; // load the grid from storage
    const controlPanel = dciGridStorage.controlPanel;

    this.entities = this.tradeCaptureService.depositTakersAndCurrencies.map(
      obj => obj.entity
    );

    this.controlPanelForm = new FormGroup({
      currency: new FormControl('', Validators.required),
      amount: new FormControl(
        controlPanel && controlPanel.amount ? controlPanel.amount : '',
        Validators.required
      ),
      startDate: new FormControl('', Validators.required),
      depositTaker: new FormControl('', [Validators.required]),
      interest: new FormControl(
        controlPanel && controlPanel.interest
          ? controlPanel.interest
          : 'Converted',
        Validators.required
      ),
      solveFor: new FormControl(
        controlPanel && controlPanel.solveFor ? controlPanel.solveFor : 'Yield',
        Validators.required
      ),
      marginBps: new FormControl(
        controlPanel && controlPanel.marginBps ? controlPanel.marginBps : ''
      )
    });

    this.controlPanelForm.get('startDate').valueChanges.subscribe(val => {
      this.startDateInfo.info = '';
      this.requestStartDate(val);
    });

    this.controlPanelForm.get('currency').valueChanges.subscribe(val => {
      if (this.allLoaded) {
        this.wipeTabs(); // wipe all tabs on storage if all are loaded
      }
      const tabStorage = this.dataStorage.tabs || {};

      if (val) {
        this.textBoxAdderPrep('amount', ' ' + val, !!val, 'currency');
        this.tabs =
          this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo
            .filter(currencyInfo => currencyInfo.currencyPair.includes(val)) // only look at currencies that contain the deposit currency
            .map((currencyInfo: CurrencyInfo, index: number) => {
              return {
                // build the tab info
                currency: currencyInfo.currencyPair.replace(val, ''), // store the other currency
                selected: index === 0 ? true : false,
                currencyPair: currencyInfo.currencyPair,
                forwardPointsMultiplier: currencyInfo.forwardPointsMultiplier,
                resultSolveFor: '',
                results: {
                  minValue: null,
                  maxValue: null,
                  values: []
                },
                startDate: { date: '', dateLag: '' },
                dates: {},
                spot: {
                  value: '',
                  freeze: false,
                  pending: false
                },
                tabStorage:
                  tabStorage[currencyInfo.currencyPair.replace(val, '')] || {}
              };
            });
        if (this.tabs.length) {
          this.setDefaultStartDate(this.tabs[0]);
        }
      } else {
        this.textBoxAdderPrep('amount', ' DEL', false, 'currency');
        this.tabs = []; // if no currency selected wipe the tabs
      }
    });

    this.controlPanelForm.get('amount').valueChanges.subscribe(val => {
      this.textBoxAdderPrep(
        'amount',
        ' ' + this.controlPanelForm.get('currency').value,
        this.controlPanelForm.get('currency').value,
        'currency'
      );
    });

    this.controlPanelForm.get('marginBps').valueChanges.subscribe(val => {
      this.textBoxAdderPrep('marginBps', '%', !!val, 'percentage');
    });

    if (controlPanel && this.entities?.includes(controlPanel.depositTaker)) {
      this.controlPanelForm
        .get('depositTaker')
        .setValue(controlPanel.depositTaker);

      this.changeInDepositCurrency(controlPanel.depositTaker);
    } else {
      this.controlPanelForm.get('depositTaker').setValue(this.entities[0]);
      this.changeInDepositCurrency(this.entities[0]);
    }

    if (controlPanel && controlPanel.currency) {
      this.controlPanelForm.get('currency').setValue(controlPanel.currency);
    }

    const parent = this.configService.userSettings().parent;
    if (parent['dci:forceInterestConversion']) {
      this.dciData.interest = ['Converted'];
    }

    this.allLoaded = true;
  }

  requestStartDate(date: string) {
    const selectedTab = this.tabs.find(x => x.selected);
    if (
      selectedTab &&
      selectedTab.currencyPair &&
      this.controlPanelForm.get('startDate').valid
    ) {
      let payload = DateUtilities.dateOrLagToPayload(
        date,
        selectedTab.currencyPair,
        this.pricingExtraService.getMarketDataTimeStamp
      );
      this.nextBusinessDay(payload, selectedTab);
    }
  }

  nextBusinessDay(
    payload: CalculateFutureDatePayload,
    selectedTab: DciTab,
    useSettlementDate?: boolean
  ) {
    // scheduleNow indicates whether a schedule should be generated.
    // calcs next business day and sets the date in passed control
    this.startDateInfo.pending = true;
    this.controlPanelForm.get('startDate').markAsPending();
    this.legService.calculateFutureDate(payload).then(
      resp => {
        this.startDataRequestSub = resp.subscribe(
          (resp: { expirationDate: string; settlementDate: string }) => {
            this.startDateInfo.pending = false;

            if (resp.expirationDate && resp.settlementDate) {
              const prettyDate = DateUtilities.dateToPrettyDate(
                useSettlementDate ? resp.settlementDate : resp.expirationDate
              );

              selectedTab.startDate.date = prettyDate;
              if (DateUtilities.isValidLag(payload.dateOrLag)) {
                this.startDateInfo.info = '(' + payload.dateOrLag + ')';
                selectedTab.startDate.dateLag = payload.dateOrLag;
              } else {
                selectedTab.startDate.dateLag = '';
              }
              this.controlPanelForm
                .get('startDate')
                .setValue(prettyDate, { emitEvent: false });

              this.dciTabComponent.updateDateDifferences(); // update the dates in the tab
            }
          },
          err => {
            this.startDateInfo.pending = false;
          }
        );
      },
      err => {
        this.startDateInfo.pending = false;
      }
    );
  }

  set setStartDate(prettyDate: string) {
    setTimeout(() => {
      this.controlPanelForm
        .get('startDate')
        .patchValue(prettyDate, { emitEvent: false });
    });
  }

  changeInDepositCurrency(entity: string, saveChanges?: boolean) {
    // when deposit currency is changed, wipe currency selected and populate with new currencies.
    this.controlPanelForm.get('currency').setValue('');
    this.currencies =
      this.tradeCaptureService.depositTakersAndCurrencies.find(
        depositTaker => depositTaker.entity === entity
      )?.currencies || [];

    if (saveChanges) {
      this.saveSpecificChange(
        'currency',
        this.controlPanelForm.get('currency').value
      );

      this.saveSpecificChange(
        'depositTaker',
        this.controlPanelForm.get('depositTaker').value
      );
    }
  }

  get isOpened() {
    return this.marketsService.showDci;
  }

  public close(): void {
    this.marketsService.showDci = false;
  }

  tabSelected(tabData: DciTab) {
    // mark tab as selected, de select other tab.
    if (this.startDataRequestSub) {
      // when switching tab un sub for the http request
      // we don't want the dates to update when switching for tab to tab
      this.startDataRequestSub.unsubscribe();
    }
    this.startDateInfo.pending = false;
    this.startDateInfo.info = '';

    const selectedTab = this.tabs.find(tab => tab.selected);
    if (selectedTab) {
      selectedTab.selected = false;
      selectedTab.spot.pending = false;
    }

    tabData.selected = true;
    this.setDefaultStartDate(tabData);
  }

  setDefaultStartDate(tabData: DciTab) {
    // sets the default start date when a tab is selected
    let payload = DateUtilities.dateOrLagToPayload(
      '0b',
      tabData.currencyPair,
      this.pricingExtraService.getMarketDataTimeStamp
    );

    if (!tabData.startDate.date && !tabData.startDate.dateLag) {
      // if no dates are set in the tab load the default lag of 0b (we take the settlement date to be 2b)
      if (tabData.currencyPair) this.nextBusinessDay(payload, tabData, true);
    } else if (
      tabData.startDate.dateLag &&
      DateUtilities.isValidLag(tabData.startDate.dateLag)
    ) {
      // if there is a date lag in the tab, load it
      payload.dateOrLag = tabData.startDate.dateLag;
      if (tabData.currencyPair) this.nextBusinessDay(payload, tabData);
    } else if (tabData.startDate.date) {
      // if there is a date set in the tab, set it in the form
      this.setStartDate = tabData.startDate.date;
    }
  }

  textBoxAdderPrep(
    elementName: string,
    textToBeAdded: string,
    addToEnd: boolean,
    typeToAdd: 'currency' | 'percentage'
  ) {
    Utilities.endOfTextBoxAdder(
      this.elementRef.nativeElement.querySelector('#' + elementName),
      this.controlPanelForm.get(elementName),
      textToBeAdded,
      addToEnd,
      typeToAdd,
      this.renderer
    );
  }

  textBoxRestrictionHandler(event, separator: string, action: string) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(
      event.target.value,
      separator
    );
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  get dataStorage(): DciTabState {
    return (
      this.userStorageService.getItem<DciTabState>(DciGridDefaultsStateKey) ||
      {}
    ); // load the grid from storage
  }

  wipeTabs() {
    // wipe tabs data in storage
    let dciGridStorage = this.dataStorage;
    dciGridStorage.tabs = {};
    this.userStorageService.setItem(DciGridDefaultsStateKey, dciGridStorage);
  }

  saveSpecificChange(attributeName: string, val: any) {
    // used to save specifics data in the control pannel
    let dciDataStorage = this.dataStorage;
    if (!dciDataStorage.controlPanel) {
      dciDataStorage.controlPanel = {};
    }

    dciDataStorage.controlPanel[attributeName] = val;
    this.userStorageService.setItem(DciGridDefaultsStateKey, dciDataStorage);
  }

  wipeAllTabInfo() {
    let dciGridStorage =
      this.userStorageService.getItem(DciGridDefaultsStateKey) || {};
    dciGridStorage['tabs'] = {};
    this.userStorageService.setItem(DciGridDefaultsStateKey, dciGridStorage);
  }

  percentageController() {}

  isDateValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (
      DateUtilities.isPrettyDate(control.value) ||
      DateUtilities.isDateOfSlashFormat(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  public dragStart(): void {
    this.dragging = true;
  }

  public async dragEnd() {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.dci-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public updateZIndex(): void {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.dci-drag'),
      this.renderer
    );
  }
}
